/**
 * Client crash reporting. Imported FIRST by client/index.js, before App.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 *
 * Google Play rejected v1.9.0 under the Broken Functionality policy — "the app
 * opens, but it keeps crashing" — and we could not see a single stack trace,
 * because this app had no client crash reporting at all. `@sentry/node` is a
 * SERVER dependency; nothing was watching the app on real devices.
 *
 * The cause turned out to be diagnosable from `expo install --check` (SDK-56
 * packages in an SDK-54 build). The next one will not be. A launch crash that
 * reaches reviewers before it reaches us is a rejection we pay for in review
 * cycles, and on a product whose whole claim is that its numbers are checkable,
 * "we don't know why it crashed" is not a position we can hold.
 *
 * ─── Two rules this file must never break ───────────────────────────────────
 *
 * 1. THE CRASH REPORTER MUST NEVER CRASH THE APP. It runs before anything else,
 *    so a throw here is an unconditional launch crash — precisely the failure it
 *    exists to report, caused by the reporter. Hence the try/catch around init
 *    and the DSN guard: no DSN means no init, silently, not an exception.
 *
 * 2. IT MUST NOT EXFILTRATE THE USER'S BOOK. This app holds broker API
 *    credentials and live position values. Session Replay is deliberately NOT
 *    enabled (it would screenshot portfolio values), `sendDefaultPii` is off,
 *    and `beforeSend` strips auth material — mirroring the scrub the server
 *    already does in server/index.ts.
 */
import { emitCrashLog, installGlobalCrashHandler, type CrashContext } from "@/lib/crashLog";
import { sendCrashBeacon } from "@/lib/crashBeacon";

/**
 * Must be EXPO_PUBLIC_* to be inlined into the client bundle at build time; a
 * bare `SENTRY_DSN` would read as undefined on device and silently disable
 * reporting, which is the same blindness we are fixing.
 *
 * A Sentry DSN is not a secret — it is a public write-only ingest endpoint,
 * designed to ship inside client binaries. The auth token used for source-map
 * upload IS secret and stays on EAS as SENTRY_AUTH_TOKEN.
 *
 * ─── Source maps are OFF, deliberately ──────────────────────────────────────
 *
 * `SENTRY_DISABLE_AUTO_UPLOAD=true` is set in eas.json's production env, and it
 * has to be: the Sentry Gradle plugin adds a `..._SentryUpload_...` task that
 * HARD-FAILS the Android build when no org is configured —
 *
 *     error: An organization ID or slug is required (provide with --org)
 *
 * which is exactly how vc16 died. (The Expo config plugin only *warns* about a
 * missing organization/project at prebuild time, so "the plugin cannot fail the
 * build" is wrong — the failure comes later, from Gradle.)
 *
 * Consequence, stated plainly: crashes ARE captured, but stack traces arrive
 * MINIFIED. That is strictly better than the blindness that let a launch crash
 * reach Play reviewers, and it is not the end state. To symbolicate, set
 * SENTRY_ORG, SENTRY_PROJECT and SENTRY_AUTH_TOKEN on EAS and remove
 * SENTRY_DISABLE_AUTO_UPLOAD from eas.json.
 */
const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;

type SentryApi = Pick<typeof import("@sentry/react-native"), "init" | "captureException">;
let sentry: Promise<SentryApi> | null = null;

const SENSITIVE_KEYS = /^(authorization|cookie|x-admin-token|api[-_]?key|secret|token|password)$/i;

function scrub(obj: Record<string, unknown> | undefined): void {
  if (!obj) return;
  for (const k of Object.keys(obj)) if (SENSITIVE_KEYS.test(k)) delete obj[k];
}

function loadSentry(): Promise<SentryApi> {
  if (!dsn) return Promise.reject(new Error("Sentry is not configured."));
  return (sentry ??= import("@sentry/react-native").then((Sentry) => {
    Sentry.init({
      dsn,
      environment: __DEV__ ? "development" : "production",
      // Crashes and errors are the point. Tracing is sampled low — this is a
      // diagnostic channel, not an analytics one, and Play Data Safety is
      // declared on that basis (crash logs + diagnostics, no advertising ID).
      tracesSampleRate: 0.05,
      sendDefaultPii: false,
      // Native crashes are the ones that got us rejected: a JS-only reporter
      // would have missed the TurboModule init failure entirely.
      enableNativeCrashHandling: true,
      // `enableAutoSessionTracking` gives crash-free-session rates, which is the
      // metric Play's own Android Vitals judges stability on.
      enableAutoSessionTracking: true,
      beforeSend(event: any) {
        try {
          if (event.request) {
            event.request.data = undefined;
            if (event.request.url) event.request.url = event.request.url.split(/[?#]/, 1)[0];
          }
          scrub(event.request?.headers as Record<string, unknown> | undefined);
          scrub(event.request?.cookies as Record<string, unknown>);
          if (event.request?.query_string) delete event.request.query_string;
          // Keep a pseudonymous correlation id at most; never email, IP, name,
          // wallet address, or an auth-provider payload.
          event.user = event.user?.id ? { id: event.user.id } : undefined;
          // Breadcrumbs carry fetch URLs; a query string can hold a token.
          for (const b of event.breadcrumbs ?? []) {
            const d = b.data as Record<string, unknown> | undefined;
            if (d && typeof d.url === "string") d.url = d.url.split("?")[0];
            scrub(d);
          }
        } catch {
          // A scrub failure must not drop the event AND must not throw. Returning
          // the event unscrubbed is worse than returning nothing, so drop it.
          return null;
        }
        return event;
      },
    });
    return Sentry;
  }));
}

// Sentry has no production DSN today. Do not make a dormant monitoring SDK a
// parse-and-execute cost for every market reader; when configured it still
// starts as soon as the app evaluates this module.
if (dsn) void loadSentry().catch(() => {});

/** True when reporting is actually live, so callers need not re-read the env. */
export const crashReportingEnabled = Boolean(dsn);

/**
 * ─── Crash visibility WITHOUT Sentry ────────────────────────────────────────
 *
 * Everything above is conditional on a DSN that, as of 2026-08-16, is set
 * nowhere — so `Sentry.init()` never runs and the whole apparatus above is
 * inert in the shipped build. That is exactly the blindness that made the
 * Broken Functionality rejection undiagnosable, and it must not be the only
 * plan.
 *
 * What follows is unconditional and needs no third-party account, because
 * Play Console attaches the device's **logcat** to a crash report. Writing the
 * JS stack to `console.error` therefore puts a readable trace in front of a
 * Google reviewer — where Hermes' own native trace is unreadable frames.
 *
 * Sentry, when a DSN eventually exists, becomes an ADDITION to this rather
 * than a replacement for it.
 */
function reportCrash(error: unknown, context: CrashContext): void {
  // Always to logcat first — this is the channel that survives having no DSN.
  emitCrashLog(error, context);

  if (!crashReportingEnabled) {
    // Web has no Android logcat. A privacy-scrubbed first-party beacon puts the
    // crash into Railway logs until a Sentry DSN is configured.
    sendCrashBeacon(error, context);
    return;
  }
  void loadSentry().then((Sentry) => {
    Sentry.captureException(error, {
      level: context.fatal ? "fatal" : "error",
      tags: { origin: context.origin },
      extra: context.componentStack ? { componentStack: context.componentStack } : undefined,
    });
  }).catch(() => {});
}

/**
 * Exported so `ErrorBoundary` reports through the SAME path. Two crash sinks
 * that can drift is the defect class we keep paying for; there is one here.
 */
export const reportCaughtError = reportCrash;

// The gap no error boundary can cover: a boundary only sees errors thrown
// during a React render, and the crash that got us rejected — a native module
// failing `TurboModuleRegistry.getEnforcing()` — happens at import time, before
// anything renders. This also catches async rejections and event-handler throws.
installGlobalCrashHandler(reportCrash);

if (!crashReportingEnabled) {
  // 🔴 Say the absence out loud. A silently disabled crash reporter looks
  // identical to a working one, and "absence renders as safety" is the defect
  // class this codebase keeps rediscovering. This line is what tells whoever
  // reads logcat that Sentry is off — the logcat path above still works.
  console.warn(
    "[FAHALI_CRASH] Sentry is DISABLED (no EXPO_PUBLIC_SENTRY_DSN). " +
      "Native crashes are logged to logcat; web crashes use the scrubbed Railway beacon. " +
      "To enable: eas env:create --environment production " +
      "--name EXPO_PUBLIC_SENTRY_DSN --value <dsn> --visibility plaintext",
  );
}
